import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveCurrencyCode } from '../../utils/currencyFormatter';

type Mode = 'clock' | 'cost';

const CLOCK_STEPS = [
  { id: null as string | null, minutes: 0, angle: 0, label: 'Start (Stundensatz & 60 Min)' },
  { id: 'node-10', minutes: 10, angle: 60, label: '10 Minuten' },
  { id: 'node-15', minutes: 15, angle: 90, label: '15 Minuten' },
  { id: 'node-20', minutes: 20, angle: 120, label: '20 Minuten' },
  { id: 'node-30', minutes: 30, angle: 180, label: '30 Minuten (Halbe Stunde)' },
  { id: 'node-40', minutes: 40, angle: 240, label: '40 Minuten' },
  { id: 'node-45', minutes: 45, angle: 270, label: '45 Minuten (Dreiviertelstunde)' },
  { id: 'node-50', minutes: 50, angle: 300, label: '50 Minuten' },
  { id: 'node-60', minutes: 60, angle: 360, label: '60 Minuten (Volle Stunde)' },
];

const COST_STEP_LABELS = [
  'Ausgangswert (Brutto-Stundensatz & Teamlohn)',
  'Schritt 1: Mehrwertsteuer abgezogen',
  'Schritt 2: Materialeinsatz (Gramm × Materialrechner) abgezogen',
  'Schritt 3: Feste Gemeinkosten abgezogen',
  'Schritt 4: Unpr. Unternehmerlohn abgezogen',
  'Schritt 5: Kalk. Verlust & Leerlauf abgezogen',
  'Schritt 6: Übrig für Lohnkosten (Deckungsbeitrag)',
  'Schritt 7: Lohn & Nebenkosten (Vollkosten)',
  'Schritt 8: Ergebnis – Reingewinn, Rücklage & Status',
];

const PRESETS = [60, 70, 75, 80, 90, 100, 120, 140, 160, 180, 200];
const CIRC = 2 * Math.PI * 400;
const UI_SCALE_MIN = 0.75;
const UI_SCALE_MAX = 1.45;
const UI_SCALE_STEP = 0.05;

function initialUiScale() {
  if (typeof window === 'undefined') return 1;
  const h = window.innerHeight || 900;
  // 15" ~800–900px Höhe → ~1.0; größere Monitore etwas größer starten
  if (h >= 1100) return 1.15;
  if (h >= 980) return 1.08;
  if (h >= 860) return 1;
  return 0.92;
}

const fmtDe = (n: number, digits = 2) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtPct = (n: number, digits = 1) =>
  `${n.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`;

export type BeratungsZeitKostenSuiteProps = {
  stundenfaktor: number;
  kalkRuecklage: number;
  storeCalc: any;
  formatCurrency: (v: number, d?: number) => string;
  initialDemoRate?: number;
  effectiveSozialabgabenPercent?: number;
  materialData?: any[];
  activeMaterialIDs?: string[];
  onToggleMaterial?: (id: string) => void;
  initialMaterialGramm?: number;
  onClose: () => void;
};

function applyExcelBehavior(el: HTMLInputElement | null) {
  if (!el) return () => {};
  const onFocus = () => setTimeout(() => el.select(), 40);
  const onClick = (e: MouseEvent) => {
    if (e.detail === 1) el.select();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') el.blur();
  };
  el.addEventListener('focus', onFocus);
  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKey);
  return () => {
    el.removeEventListener('focus', onFocus);
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKey);
  };
}

export default function BeratungsZeitKostenSuite({
  stundenfaktor,
  kalkRuecklage,
  storeCalc,
  formatCurrency,
  initialDemoRate,
  effectiveSozialabgabenPercent = 0,
  materialData = [],
  activeMaterialIDs: activeMaterialIDsProp = [],
  onToggleMaterial,
  initialMaterialGramm = 0,
  onClose,
}: BeratungsZeitKostenSuiteProps) {
  const startRate =
    initialDemoRate && initialDemoRate > 0
      ? initialDemoRate
      : stundenfaktor > 0
        ? stundenfaktor
        : 75;

  const [mode, setMode] = useState<Mode>('clock');
  const [rate, setRate] = useState(startRate);
  const [clockStep, setClockStep] = useState(0);
  const [costStep, setCostStep] = useState(0);
  /** 0 = keine manuelle Anzeige auf der Uhr (verdeckt sonst Presets) */
  const [customMinutes, setCustomMinutes] = useState(0);
  const [matGrams, setMatGrams] = useState(
    initialMaterialGramm > 0 ? initialMaterialGramm : 0
  );
  /** Lokale Aktivierung, falls Parent keinen Toggle liefert */
  const [localActiveIds, setLocalActiveIds] = useState<string[]>(() => [
    ...activeMaterialIDsProp,
  ]);
  const activeMaterialIDs = onToggleMaterial ? activeMaterialIDsProp : localActiveIds;
  const toggleMaterial = (id: string) => {
    if (onToggleMaterial) {
      onToggleMaterial(id);
      return;
    }
    setLocalActiveIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [uiScale, setUiScale] = useState(initialUiScale);
  const dragRef = useRef<{ active: boolean; ox: number; oy: number }>({
    active: false,
    ox: 0,
    oy: 0,
  });

  const adjustUiScale = (delta: number) => {
    setUiScale((s) =>
      Math.round(Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN, s + delta)) * 100) / 100
    );
  };

  const rateClockRef = useRef<HTMLInputElement>(null);
  const rateCostRef = useRef<HTMLInputElement>(null);
  const gramsRef = useRef<HTMLInputElement>(null);
  const minsRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const cleanups = [
      applyExcelBehavior(rateClockRef.current),
      applyExcelBehavior(rateCostRef.current),
      applyExcelBehavior(gramsRef.current),
      applyExcelBehavior(minsRef.current),
    ];
    return () => cleanups.forEach((c) => c());
  }, [mode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.active) return;
      setPos({ x: e.clientX - dragRef.current.ox, y: e.clientY - dragRef.current.oy });
    };
    const onUp = () => {
      dragRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const money = (v: number) => {
    const raw = formatCurrency(v);
    return raw.replace(/€/g, 'EUR').replace(/CHF/g, 'CHF');
  };

  /** ISO-Kürzel neben Stundensatz-Eingabe / Presets (nie €-Symbol) */
  const currencyCode = useMemo(() => {
    const fromStore = resolveCurrencyCode(
      storeCalc?.currencyCode,
      storeCalc?.activeLand
    );
    if (fromStore === 'CHF') return 'CHF';
    const sample = formatCurrency(0);
    return /CHF/i.test(sample) ? 'CHF' : 'EUR';
  }, [storeCalc?.currencyCode, storeCalc?.activeLand, formatCurrency]);

  /** Einzeilig für Zeituhr-Nodes (kein Umbruch vor EUR/CHF) */
  const moneyTight = (v: number) => money(v).replace(/\s+/g, '\u00A0');

  const factors = useMemo(() => {
    const resolvedMwstPct =
      storeCalc?.mwstSatz !== undefined && storeCalc?.mwstSatz !== null
        ? Number(storeCalc.mwstSatz)
        : 19;
    const mwst = resolvedMwstPct / 100;
    const gk = storeCalc?.gemeinkostenfaktor || 0;
    const ul = (storeCalc?.unprodUnternehmerlohnPercent || 0) / 100;
    const vl = (storeCalc?.settings?.global?.Verlust_1 || 0) / 100;
    const wawiVk = Number(storeCalc?.settings?.global?.Warenwirtschaftsaufschlag_VK);
    const matFaktor = Number.isFinite(wawiVk) && wawiVk > 0 ? wawiVk : 1.3;
    const teamBase = Number(storeCalc?.teamStundenlohn || 0);
    const urlPct = Number(storeCalc?.lohnfortzahlungPercent || 0);
    const svPct = Number(effectiveSozialabgabenPercent || 0);
    const lohnBasis = Number(teamBase.toFixed(2));
    const urlaubVal = Number((lohnBasis * (urlPct / 100)).toFixed(2));
    const sozialVal = Number(((lohnBasis + urlaubVal) * (svPct / 100)).toFixed(2));
    const teamTotal = Number((lohnBasis + urlaubVal + sozialVal).toFixed(2));
    const liquiditaet = Number(storeCalc?.settings?.global?.Liquiditaet || 10);

    // Ø/100g nur aus aktivierten Materialrechner-Produkten
    let mSum = 0;
    let mCnt = 0;
    const list = materialData || [];
    (activeMaterialIDs || []).forEach((id) => {
      const m = list.find((x: any) => x.id === id);
      if (m && Number(m.gebinde) > 0) {
        mSum += (Number(m.ekPreis) / Number(m.gebinde)) * 100;
        mCnt++;
      }
    });
    const avg100 = mCnt > 0 ? mSum / mCnt : 0;

    return {
      mwst,
      mwstPct: resolvedMwstPct,
      gk,
      gkPct: gk * 100,
      ul,
      ulPct: storeCalc?.unprodUnternehmerlohnPercent || 0,
      vl,
      vlPct: storeCalc?.settings?.global?.Verlust_1 || 0,
      matFaktor,
      teamBase: lohnBasis,
      urlaubVal,
      sozialVal,
      teamTotal,
      urlPct,
      svPct,
      avg100,
      matCount: mCnt,
      liquiditaet,
    };
  }, [storeCalc, materialData, activeMaterialIDs, effectiveSozialabgabenPercent]);

  const calc = useMemo(() => {
    const activeRate = Math.max(5, rate || 0);
    const netto = Math.round((activeRate / (1 + factors.mwst)) * 100) / 100;
    const mwstVal = Math.round((activeRate - netto) * 100) / 100;

    const grams = Math.max(0, matGrams || 0);
    const matTotal =
      grams > 0
        ? Math.round(((factors.avg100 / 100) * grams * factors.matFaktor) * 100) / 100
        : 0;

    const overhead = Math.round(netto * factors.gk * 100) / 100;
    const ownerWage = Math.round(netto * factors.ul * 100) / 100;
    const loss = Math.round(netto * factors.vl * 100) / 100;
    const subtotal =
      Math.round((netto - matTotal - overhead - ownerWage - loss) * 100) / 100;
    const finalProfit = Math.round((subtotal - factors.teamTotal) * 100) / 100;
    const marginPercent =
      netto > 0 ? Math.round((finalProfit / netto) * 1000) / 10 : 0;

    return {
      activeRate,
      netto,
      mwstVal,
      matTotal,
      grams,
      overhead,
      ownerWage,
      loss,
      subtotal,
      finalProfit,
      marginPercent,
      minRate: activeRate / 60,
    };
  }, [rate, matGrams, factors]);

  const setGlobalRate = (val: number) => {
    setRate(Math.max(5, Math.min(999, val)));
  };

  const adjustRate = (delta: number) => {
    const snapped = Math.round(rate / 5) * 5;
    setGlobalRate(snapped + delta);
  };

  const nextStep = () => {
    if (mode === 'clock') setClockStep((s) => Math.min(s + 1, CLOCK_STEPS.length - 1));
    else setCostStep((s) => Math.min(s + 1, COST_STEP_LABELS.length - 1));
  };
  const prevStep = () => {
    if (mode === 'clock') setClockStep((s) => Math.max(s - 1, 0));
    else setCostStep((s) => Math.max(s - 1, 0));
  };
  const resetStep = () => {
    if (mode === 'clock') setClockStep(0);
    else setCostStep(0);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (
        e.key === 'ArrowRight' ||
        e.key === 'PageDown' ||
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'ArrowDown' ||
        e.key === 'Enter' ||
        e.key === 'n'
      ) {
        e.preventDefault();
        nextStep();
      } else if (
        e.key === 'ArrowLeft' ||
        e.key === 'PageUp' ||
        e.key === 'ArrowUp' ||
        e.key === 'Backspace' ||
        e.key === 'p'
      ) {
        e.preventDefault();
        prevStep();
      } else if (e.key === 'Home' || e.key === 'r') {
        e.preventDefault();
        resetStep();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const stepLabel =
    mode === 'clock' ? CLOCK_STEPS[clockStep].label : COST_STEP_LABELS[costStep];
  const clockObj = CLOCK_STEPS[clockStep];
  const progressOffset = CIRC * (1 - clockObj.angle / 360);

  const resultStatus = (() => {
    if (calc.finalProfit < 0) {
      return {
        cls: 'status-red',
        badge: 'MINUS / VERLUST',
        desc: 'Der Stundensatz deckt die Kosten nicht ab. Jede Stunde erzeugt ein rechnerisches Defizit.',
      };
    }
    if (calc.marginPercent < 5) {
      return {
        cls: 'status-orange',
        badge: 'GEFAHRENZONE (< 5 %)',
        desc: 'Sehr dünnes Polster unter 5 %. Schon kleine Ausfälle belasten die Liquidität.',
      };
    }
    if (calc.marginPercent < factors.liquiditaet) {
      return {
        cls: 'status-yellow',
        badge: 'KOSTENDECKEND',
        desc: `Solide Deckung — zum Ziel von mind. ${fmtPct(factors.liquiditaet, 0)} Rücklage fehlen noch Reserven.`,
      };
    }
    if (calc.marginPercent < 15) {
      return {
        cls: 'status-green',
        badge: 'ZIEL ERREICHT / STABIL',
        desc: `Mindestens ${fmtPct(Math.max(factors.liquiditaet, kalkRuecklage), 0)} Rücklage erreicht. Stabile Liquidität gesichert.`,
      };
    }
    return {
      cls: 'status-purple',
      badge: 'EXZELLENT / RENTABEL',
      desc: 'Über 15 % Rücklage sichern maximale Krisenfestigkeit und unternehmerische Freiheit.',
    };
  })();

  const handleDragStart = (e: React.MouseEvent) => {
    const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'button' || tag === 'input') return;
    dragRef.current = { active: true, ox: e.clientX - pos.x, oy: e.clientY - pos.y };
    e.preventDefault();
  };

  const nodePositions: Record<string, { top: string; left: string }> = {
    'node-60': { top: '10%', left: '50%' },
    'node-10': { top: '30%', left: '83%' },
    'node-15': { top: '50%', left: '87%' },
    'node-20': { top: '70%', left: '83%' },
    'node-30': { top: '90%', left: '50%' },
    'node-40': { top: '70%', left: '17%' },
    'node-45': { top: '50%', left: '13%' },
    'node-50': { top: '30%', left: '17%' },
  };

  /** Bedienzeit außerhalb des Kreises; Winkel wie Presets (60 Min = 360° oben) */
  const showCustomNode = customMinutes > 0;
  const customClockAngle =
    customMinutes <= 0
      ? 0
      : customMinutes % 60 === 0
        ? 360
        : (customMinutes % 60) * 6;
  const customRad = (customClockAngle * Math.PI) / 180;
  /** Ring ≈ 40 %, Marker weiter außen ≈ 56 % — mit Verbindungsstrich */
  const CUSTOM_RING_R = 40;
  /** Weiter außen + kleineres Schild → weniger Überdeckung der Presets */
  const CUSTOM_NODE_R = 60;
  const customNodePos = {
    top: `${50 - CUSTOM_NODE_R * Math.cos(customRad)}%`,
    left: `${50 + CUSTOM_NODE_R * Math.sin(customRad)}%`,
  };
  const customLine = {
    x1: 50 + CUSTOM_RING_R * Math.sin(customRad),
    y1: 50 - CUSTOM_RING_R * Math.cos(customRad),
    x2: 50 + (CUSTOM_NODE_R - 4) * Math.sin(customRad),
    y2: 50 - (CUSTOM_NODE_R - 4) * Math.cos(customRad),
  };
  const customPrice = calc.minRate * customMinutes;

  return createPortal(
    <div
      className="pe-zk-suite fixed z-[99999] w-[97vw] max-w-[1240px] max-h-[94vh] overflow-hidden rounded-xl border border-[rgba(220,182,138,0.35)] shadow-[0_20px_60px_rgba(0,0,0,0.65)] flex flex-col"
      style={{
        left: `calc(50% + ${pos.x}px)`,
        top: `calc(36px + ${pos.y}px)`,
        transform: 'translateX(-50%)',
      }}
      role="dialog"
      aria-label="Zeit & Kosten Suite"
      onClick={(e) => {
        const t = e.target as HTMLElement;
        if (
          t.closest('.pe-zk-hub') ||
          t.closest('.pe-zk-banner') ||
          t.closest('.pe-zk-custom') ||
          t.closest('.pe-zk-controls') ||
          t.closest('.pe-zk-presets') ||
          t.closest('.pe-zk-zoom') ||
          t.closest('.pe-zk-close') ||
          t.closest('.pe-zk-top-actions') ||
          t.closest('.pe-zk-footer') ||
          t.closest('.pe-zk-node') ||
          t.closest('.pe-zk-tabs') ||
          t.closest('.pe-zk-drag') ||
          t.closest('input') ||
          t.closest('button') ||
          t.closest('.pe-zk-deduct') ||
          t.closest('.pe-zk-mat-list') ||
          t.closest('.pe-zk-side')
        ) {
          return;
        }
        nextStep();
      }}
    >
      <style>{`
        .pe-zk-suite {
          --zk-bg: #0a0a0a;
          --zk-panel: rgba(18,18,18,0.92);
          --zk-text: #f8fafc;
          --zk-muted: #94a3b8;
          --zk-gold: #dcb68a;
          --zk-border: rgba(220,182,138,0.35);
          font-family: 'Segoe UI', system-ui, sans-serif;
          color: var(--zk-text);
          background:
            radial-gradient(circle at center, rgba(220,182,138,0.04) 0%, rgba(10,10,10,0.98) 70%),
            linear-gradient(135deg, #141414 25%, #0a0a0a 25%, #0a0a0a 50%, #141414 50%, #141414 75%, #0a0a0a 75%, #0a0a0a 100%);
          background-size: 100% 100%, 12px 12px;
          user-select: none;
        }
        html:not(.dark) .pe-zk-suite {
          --zk-bg: #f7f4ef;
          --zk-panel: #ffffff;
          --zk-text: #1c1917;
          --zk-muted: #44403c;
          --zk-gold: #7a4f24;
          --zk-border: rgba(90, 60, 30, 0.35);
          background:
            radial-gradient(circle at center, rgba(166,124,82,0.1) 0%, rgba(247,244,239,0.98) 70%),
            linear-gradient(135deg, #ebe6df 25%, #f7f4ef 25%, #f7f4ef 50%, #ebe6df 50%, #ebe6df 75%, #f7f4ef 75%, #f7f4ef 100%);
          background-size: 100% 100%, 12px 12px;
        }
        html:not(.dark) .pe-zk-suite .pe-zk-panel-dark,
        html:not(.dark) .pe-zk-suite .pe-zk-banner,
        html:not(.dark) .pe-zk-suite .pe-zk-custom,
        html:not(.dark) .pe-zk-suite .pe-zk-team {
          background: #ffffff !important;
          color: #1c1917;
          border-color: rgba(90, 60, 30, 0.28) !important;
        }
        html:not(.dark) .pe-zk-suite .pe-zk-footer {
          background: #efe9e0 !important;
          border-color: rgba(90, 60, 30, 0.25);
        }
        html:not(.dark) .pe-zk-suite .pe-zk-presets {
          background: #ffffff !important;
        }
        html:not(.dark) .pe-zk-suite .pe-zk-zoom {
          background: #ffffff !important;
        }
        html:not(.dark) .pe-zk-suite .deduct-card {
          background: #ffffff;
          color: #1c1917;
        }
        html:not(.dark) .pe-zk-suite .deduct-card .zk-muted,
        html:not(.dark) .pe-zk-suite .pe-zk-team .zk-muted,
        html:not(.dark) .pe-zk-suite .final-card .zk-muted {
          color: #44403c !important;
        }
        html:not(.dark) .pe-zk-suite .final-card {
          background: #ffffff;
          color: #1c1917;
        }
        html:not(.dark) .pe-zk-suite .final-card p {
          color: #44403c !important;
        }
        html:not(.dark) .pe-zk-suite .text-red-400 { color: #b91c1c !important; }
        html:not(.dark) .pe-zk-suite .text-slate-300 { color: #44403c !important; }
        html:not(.dark) .pe-zk-suite .pe-zk-tabs > div {
          background: #ffffff !important;
          border-color: rgba(90, 60, 30, 0.3) !important;
        }
        html:not(.dark) .pe-zk-suite .nav-btn {
          color: #7a4f24;
          border-color: #7a4f24;
        }
        html:not(.dark) .pe-zk-suite .preset-btn { color: #44403c; }
        html:not(.dark) .pe-zk-suite .pe-zk-footer .zk-muted { color: #44403c !important; }
        .pe-zk-suite * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        .pe-zk-suite .zk-gold { color: var(--zk-gold); }
        .pe-zk-suite .zk-muted { color: var(--zk-muted); }
        .pe-zk-suite .pe-zk-top-actions {
          position: absolute; top: 8px; right: 8px; z-index: 40;
          display: flex; align-items: center; gap: 6px;
        }
        .pe-zk-suite .pe-zk-close {
          width: 36px; height: 36px; border-radius: 9999px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid var(--zk-border); background: rgba(10,10,10,0.88);
          color: var(--zk-text); font-size: 1.05rem; cursor: pointer;
          box-shadow: 0 4px 16px rgba(0,0,0,0.45); backdrop-filter: blur(8px);
        }
        .pe-zk-suite .pe-zk-close:hover { background: var(--zk-gold); color: #0a0a0a; }
        html:not(.dark) .pe-zk-suite .pe-zk-close { background: rgba(255,255,255,0.92); }
        .pe-zk-suite .pe-zk-top-actions .pe-zk-zoom {
          height: 36px; box-shadow: 0 4px 16px rgba(0,0,0,0.35); backdrop-filter: blur(8px);
        }
        .pe-zk-suite .tab-btn {
          background: transparent; border: none; color: var(--zk-muted);
          font-size: 0.78rem; font-weight: 700; padding: 5px 12px; border-radius: 16px; cursor: pointer;
        }
        .pe-zk-suite .tab-btn.active {
          background: var(--zk-gold); color: #0a0a0a;
          box-shadow: 0 2px 10px rgba(220,182,138,0.35);
        }
        .pe-zk-suite .step-btn {
          background: rgba(220,182,138,0.1); border: 1px solid var(--zk-border);
          color: var(--zk-gold); width: 30px; height: 30px; border-radius: 50%;
          font-size: 1.1rem; font-weight: 900; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .pe-zk-suite .step-btn:hover { background: var(--zk-gold); color: #0a0a0a; }
        .pe-zk-suite .rate-input {
          background: transparent; border: none; outline: none; color: var(--zk-gold);
          font-size: clamp(1.75rem, 4vw, 2.8rem); font-weight: 900; width: 100px; text-align: center;
          border-bottom: 2px dashed rgba(220,182,138,0.35); font-family: inherit;
          -moz-appearance: textfield; appearance: textfield;
        }
        .pe-zk-suite .rate-input::-webkit-outer-spin-button,
        .pe-zk-suite .rate-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .pe-zk-suite .clock-stage {
          position: relative; width: min(460px, 78vw); height: min(460px, 78vw);
          margin: 12px auto 14px; display: flex; align-items: center; justify-content: center;
          overflow: visible;
        }
        .pe-zk-suite .clock-spoke-svg {
          position: absolute; inset: 0; width: 100%; height: 100%;
          overflow: visible; pointer-events: none; z-index: 12;
        }
        .pe-zk-suite .clock-spoke-line {
          fill: none; stroke: rgba(56, 189, 248, 0.85); stroke-width: 2.5;
          stroke-linecap: round;
          filter: drop-shadow(0 0 4px rgba(56, 189, 248, 0.55));
        }
        /* Zurückgelassene Striche beim Weiter-Wandern der Zeituhr */
        .pe-zk-suite .clock-trail-line {
          fill: none;
          stroke: rgba(148, 163, 184, 0.55);
          stroke-width: 2;
          stroke-linecap: round;
          stroke-dasharray: 5 5;
        }
        .pe-zk-suite .clock-trail-dot {
          fill: rgba(148, 163, 184, 0.7);
        }
        .pe-zk-suite .clock-svg { position: absolute; inset: 0; width: 100%; height: 100%; transform: rotate(-90deg); }
        .pe-zk-suite .clock-track { fill: none; stroke: rgba(220,182,138,0.12); stroke-width: 8; }
        .pe-zk-suite .clock-progress {
          fill: none; stroke: var(--zk-gold); stroke-width: 10; stroke-linecap: round;
          stroke-dasharray: ${CIRC}; transition: stroke-dashoffset 0.55s cubic-bezier(0.4,0,0.2,1);
          filter: drop-shadow(0 0 8px rgba(220,182,138,0.4));
        }
        .pe-zk-suite .clock-hand {
          position: absolute; width: 3px; height: 38%;
          background: linear-gradient(to top, rgba(220,182,138,0.1), var(--zk-gold));
          bottom: 50%; left: calc(50% - 1.5px); transform-origin: bottom center;
          border-radius: 3px 3px 0 0; opacity: 0;
          transition: transform 0.55s cubic-bezier(0.4,0,0.2,1), opacity 0.3s;
        }
        .pe-zk-suite .clock-hand.visible { opacity: 1; }
        .pe-zk-suite .pe-zk-node {
          position: absolute; transform: translate(-50%,-50%) scale(0.85);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: var(--zk-panel); border: 1px solid var(--zk-border);
          border-radius: 8px; padding: 5px 12px; min-width: 98px; width: max-content;
          text-align: center; box-sizing: border-box;
          opacity: 0; transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1); cursor: pointer; z-index: 15;
        }
        .pe-zk-suite .pe-zk-node.visible { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        .pe-zk-suite .pe-zk-node.active {
          border-color: var(--zk-gold);
          box-shadow: 0 0 16px rgba(220,182,138,0.35);
        }
        /* Manuelle Bedienzeit — kompakt, außerhalb, weniger Überdeckung */
        .pe-zk-suite .pe-zk-node.custom {
          border-color: rgba(56, 189, 248, 0.7);
          background: linear-gradient(160deg, rgba(12, 40, 58, 0.98), rgba(8, 22, 34, 0.98));
          box-shadow: 0 0 10px rgba(56, 189, 248, 0.35), inset 0 0 8px rgba(56, 189, 248, 0.06);
          z-index: 18;
          min-width: 0;
          padding: 3px 7px !important;
          border-radius: 6px !important;
          transform: translate(-50%, -50%) scale(0.92) !important;
        }
        .pe-zk-suite .pe-zk-node.custom.visible {
          transform: translate(-50%, -50%) scale(0.92) !important;
        }
        .pe-zk-suite .pe-zk-node.custom .zk-node-label {
          color: #7dd3fc !important;
          letter-spacing: 0.04em;
          font-size: 0.52rem !important;
          margin-bottom: 0 !important;
        }
        .pe-zk-suite .pe-zk-node.custom .zk-node-price {
          color: #38bdf8 !important;
          font-size: 0.72rem !important;
        }
        .pe-zk-suite .pe-zk-node.custom .zk-node-badge {
          margin-top: 1px;
          font-size: 0.45rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #bae6fd;
          opacity: 0.85;
          line-height: 1;
        }
        .pe-zk-suite .pe-zk-node .zk-node-label {
          white-space: nowrap; line-height: 1.15; margin-bottom: 1px;
        }
        .pe-zk-suite .pe-zk-node .zk-node-price {
          white-space: nowrap; line-height: 1.15;
          font-variant-numeric: tabular-nums;
        }
        .pe-zk-suite .pe-zk-hub {
          position: relative; z-index: 30; width: clamp(160px, 28vw, 220px);
          height: clamp(160px, 28vw, 220px); border-radius: 50%;
          background: radial-gradient(circle, #1a1a1a 0%, #101010 80%, #0a0a0a 100%);
          border: 2px solid var(--zk-border);
          box-shadow: 0 8px 28px rgba(0,0,0,0.5), inset 0 0 16px rgba(220,182,138,0.08);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          text-align: center; padding: 6px;
        }
        html:not(.dark) .pe-zk-suite .pe-zk-hub {
          background: radial-gradient(circle, #fff 0%, #f5f0ea 80%, #ebe4db 100%);
        }
        .pe-zk-suite .deduct-card {
          background: var(--zk-panel); border: 1px solid rgba(255,255,255,0.08);
          border-left: 3px solid #64748b; border-radius: 8px; padding: 6px 10px;
          display: flex; justify-content: space-between; align-items: center;
          opacity: 0.28; filter: grayscale(0.8); transform: translateY(4px);
          transition: all 0.35s ease;
        }
        html:not(.dark) .pe-zk-suite .deduct-card { border-color: rgba(0,0,0,0.06); }
        .pe-zk-suite .deduct-card.visible { opacity: 1; filter: none; transform: none; }
        .pe-zk-suite .deduct-card.tax { border-left-color: #f97316; }
        .pe-zk-suite .deduct-card.mat { border-left-color: #38bdf8; }
        .pe-zk-suite .deduct-card.overhead { border-left-color: #eab308; }
        .pe-zk-suite .deduct-card.wage-owner { border-left-color: var(--zk-gold); }
        .pe-zk-suite .deduct-card.loss { border-left-color: #ef4444; }
        .pe-zk-suite .deduct-card.subtotal {
          background: rgba(220,182,138,0.08); border: 1px solid var(--zk-border);
          border-left: 4px solid var(--zk-gold);
        }
        .pe-zk-suite .deduct-card.wage-team {
          background: rgba(168,85,247,0.06); border: 1px solid rgba(168,85,247,0.3);
          border-left: 4px solid #a855f7;
        }
        .pe-zk-suite .final-card {
          background: var(--zk-panel); border: 2px solid #334155; border-radius: 12px;
          padding: 12px; text-align: center; opacity: 0.25; filter: grayscale(0.9);
          transform: scale(0.97); transition: all 0.45s cubic-bezier(0.34,1.56,0.64,1);
        }
        .pe-zk-suite .final-card.visible { opacity: 1; filter: none; transform: scale(1); }
        .pe-zk-suite .final-card.status-red { border-color: #ef4444; box-shadow: 0 0 20px rgba(239,68,68,0.3); }
        .pe-zk-suite .final-card.status-orange { border-color: #f97316; box-shadow: 0 0 20px rgba(249,115,22,0.3); }
        .pe-zk-suite .final-card.status-yellow { border-color: #eab308; box-shadow: 0 0 20px rgba(234,179,8,0.3); }
        .pe-zk-suite .final-card.status-green { border-color: #22c55e; box-shadow: 0 0 20px rgba(34,197,94,0.3); }
        .pe-zk-suite .final-card.status-purple { border-color: #a855f7; box-shadow: 0 0 20px rgba(168,85,247,0.35); }
        .pe-zk-suite .nav-btn {
          background: transparent; border: 1px solid var(--zk-gold); color: var(--zk-gold);
          padding: 5px 10px; font-size: 0.78rem; cursor: pointer; border-radius: 6px; font-weight: 700;
        }
        .pe-zk-suite .nav-btn:hover { background: var(--zk-gold); color: #0a0a0a; }
        .pe-zk-suite .preset-btn {
          background: transparent; border: 1px solid rgba(220,182,138,0.25); color: var(--zk-muted);
          padding: 3px 8px; font-size: 0.72rem; font-weight: 700; cursor: pointer; border-radius: 12px; white-space: nowrap;
        }
        .pe-zk-suite .preset-btn.active, .pe-zk-suite .preset-btn:hover {
          background: var(--zk-gold); color: #0a0a0a; border-color: var(--zk-gold);
        }
        .pe-zk-suite .mat-input {
          width: 60px; background: var(--zk-bg); border: 1px solid var(--zk-border);
          border-radius: 6px; color: var(--zk-gold); font-size: 0.85rem; font-weight: 800;
          padding: 2px 5px; text-align: center; outline: none;
          -moz-appearance: textfield; appearance: textfield;
        }
        .pe-zk-suite .mat-input::-webkit-outer-spin-button,
        .pe-zk-suite .mat-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .pe-zk-suite .pe-zk-zoom {
          display: inline-flex; align-items: center; gap: 4px;
          border: 1px solid var(--zk-border); border-radius: 8px; padding: 2px 6px;
          background: rgba(15,15,15,0.85);
        }
        .pe-zk-suite .pe-zk-zoom button {
          background: transparent; border: none; color: var(--zk-gold);
          font-weight: 900; font-size: 0.95rem; width: 24px; height: 24px; cursor: pointer;
          border-radius: 4px;
        }
        .pe-zk-suite .pe-zk-zoom button:hover { background: rgba(220,182,138,0.2); }
        .pe-zk-suite .pe-zk-zoom button:disabled { opacity: 0.35; cursor: default; }
        .pe-zk-suite .pe-zk-zoom span {
          min-width: 38px; text-align: center; font-size: 0.72rem; font-weight: 800; color: var(--zk-muted);
        }
      `}</style>

      <div className="pe-zk-top-actions" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pe-zk-zoom" title="Darstellung verkleinern / vergrößern">
          <button
            type="button"
            disabled={uiScale <= UI_SCALE_MIN}
            onClick={(e) => {
              e.stopPropagation();
              adjustUiScale(-UI_SCALE_STEP);
            }}
            aria-label="Verkleinern"
          >
            −
          </button>
          <span>{Math.round(uiScale * 100)}%</span>
          <button
            type="button"
            disabled={uiScale >= UI_SCALE_MAX}
            onClick={(e) => {
              e.stopPropagation();
              adjustUiScale(UI_SCALE_STEP);
            }}
            aria-label="Vergrößern"
          >
            +
          </button>
        </div>
        <button
          type="button"
          className="pe-zk-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          title="Schließen"
          aria-label="Schließen"
        >
          ✕
        </button>
      </div>

      <div
        className="pe-zk-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        style={{ zoom: uiScale } as React.CSSProperties}
      >
      <div className="pe-zk-drag cursor-move px-3 pt-2 pb-1.5 pr-[148px] flex items-center gap-2 border-b border-[rgba(220,182,138,0.2)]" onMouseDown={handleDragStart}>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black uppercase tracking-[0.16em] zk-gold/90">
            Gott-Modus · Zeit & Kosten · ziehen
          </div>
          <h2 className="text-sm sm:text-base font-semibold zk-gold pointer-events-none leading-tight">
            Zeituhr & Kosten-Kaskade
          </h2>
        </div>
      </div>

      <div className="pe-zk-tabs flex justify-center px-2 pt-1.5">
        <div className="inline-flex gap-1 rounded-full p-0.5 border border-[rgba(220,182,138,0.3)] bg-[rgba(18,18,18,0.7)] backdrop-blur">
          <button
            type="button"
            className={`tab-btn ${mode === 'clock' ? 'active' : ''}`}
            onClick={() => setMode('clock')}
          >
            Zeituhr
          </button>
          <button
            type="button"
            className={`tab-btn ${mode === 'cost' ? 'active' : ''}`}
            onClick={() => setMode('cost')}
          >
            Kosten-Kaskade
          </button>
        </div>
      </div>

      <div className="px-2.5 sm:px-4 py-1.5 pb-3">
        {mode === 'clock' && (
          <div className="flex flex-col items-center animate-[fadeIn_0.35s_ease-out]">
            <div className="clock-stage">
              <svg className="clock-svg" viewBox="0 0 1000 1000">
                <circle className="clock-track" cx="500" cy="500" r="400" />
                <circle
                  className="clock-progress"
                  cx="500"
                  cy="500"
                  r="400"
                  style={{ strokeDashoffset: progressOffset }}
                />
              </svg>
              <div
                className={`clock-hand ${clockStep > 0 ? 'visible' : ''}`}
                style={{ transform: `rotate(${clockObj.angle}deg)` }}
              />
              {/* Trail: gestrichelte Striche an bereits besuchten Minuten — Uhr bleibt „gefüllt“ */}
              {clockStep > 1 && (
                <svg className="clock-spoke-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                  {CLOCK_STEPS.filter((s) => s.id).map((s, idx) => {
                    const stepIdx = idx + 1;
                    // Nur zurückliegende Schritte (aktueller = goldene Hand)
                    if (stepIdx >= clockStep) return null;
                    const angle = s.angle >= 360 ? 360 : s.angle;
                    const rad = (angle * Math.PI) / 180;
                    const x2 = 50 + 38 * Math.sin(rad);
                    const y2 = 50 - 38 * Math.cos(rad);
                    return (
                      <g key={`trail-${s.id}`}>
                        <line className="clock-trail-line" x1={50} y1={50} x2={x2} y2={y2} />
                        <circle className="clock-trail-dot" cx={x2} cy={y2} r="1.05" />
                      </g>
                    );
                  })}
                </svg>
              )}
              {CLOCK_STEPS.filter((s) => s.id).map((s, idx) => {
                const stepIdx = idx + 1;
                const is60 = s.id === 'node-60';
                const visible = is60 || stepIdx <= clockStep;
                const active = is60 ? clockStep === 8 : stepIdx === clockStep;
                const posN = nodePositions[s.id!];
                return (
                  <div
                    key={s.id!}
                    className={`pe-zk-node ${visible ? 'visible' : ''} ${active ? 'active' : ''} ${is60 ? 'top-60' : ''}`}
                    style={posN}
                    onClick={() => setClockStep(is60 ? 8 : stepIdx)}
                  >
                    <div className="zk-node-label text-[0.65rem] font-bold uppercase tracking-wide zk-muted">
                      {s.minutes} Min{s.minutes === 60 ? ' (1h)' : ''}
                    </div>
                    <div className="zk-node-price text-[0.98rem] font-black zk-gold font-numeric">
                      {moneyTight(calc.minRate * s.minutes)}
                    </div>
                  </div>
                );
              })}
              {/* Manuelle Bedienzeit — nur wenn > 0, außerhalb des Rings + Strich */}
              {showCustomNode && (
                <>
                  <svg className="clock-spoke-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                    <line
                      className="clock-spoke-line"
                      x1={customLine.x1}
                      y1={customLine.y1}
                      x2={customLine.x2}
                      y2={customLine.y2}
                    />
                    <circle cx={customLine.x1} cy={customLine.y1} r="1.1" fill="#38bdf8" />
                  </svg>
                  <div
                    className="pe-zk-node custom visible"
                    style={customNodePos}
                    title={`Manuelle Bedienzeit: ${customMinutes} Min`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const nearest = CLOCK_STEPS.filter((s) => s.id).reduce((best, s) => {
                        const d = Math.abs(s.minutes - (customMinutes % 60 || 60));
                        const bd = Math.abs((best?.minutes ?? 999) - (customMinutes % 60 || 60));
                        return d < bd ? s : best;
                      }, null as (typeof CLOCK_STEPS)[number] | null);
                      if (nearest?.id === 'node-60') setClockStep(8);
                      else if (nearest) {
                        const idx = CLOCK_STEPS.findIndex((s) => s.id === nearest.id);
                        if (idx >= 0) setClockStep(idx);
                      }
                    }}
                  >
                    <div className="zk-node-label font-bold uppercase tracking-wide">
                      {customMinutes} Min
                    </div>
                    <div className="zk-node-price font-black font-numeric">
                      {moneyTight(customPrice)}
                    </div>
                    <div className="zk-node-badge">manuell</div>
                  </div>
                </>
              )}
              <div className="pe-zk-hub" onClick={(e) => e.stopPropagation()}>
                <div className="text-[0.6rem] font-bold uppercase tracking-wider zk-muted mb-0.5">
                  Stundensatz
                </div>
                <div className="flex items-center justify-center gap-1.5">
                  <button type="button" className="step-btn" onClick={() => adjustRate(-5)}>
                    −
                  </button>
                  <div className="flex items-baseline">
                    <input
                      ref={rateClockRef}
                      type="number"
                      className="rate-input"
                      value={rate}
                      min={5}
                      max={999}
                      step={5}
                      onChange={(e) => setGlobalRate(parseFloat(e.target.value) || 5)}
                    />
                    <span className="zk-gold text-base font-black ml-0.5">{currencyCode}</span>
                  </div>
                  <button type="button" className="step-btn" onClick={() => adjustRate(5)}>
                    +
                  </button>
                </div>
                <div className="mt-1 text-[0.68rem] font-semibold px-2 py-0.5 rounded-full border border-[rgba(220,182,138,0.3)] bg-[rgba(220,182,138,0.08)]">
                  1 Min = {money(calc.minRate)}
                </div>
                {stundenfaktor > 0 && (
                  <div className="mt-1 text-[0.58rem] zk-muted">
                    Soll {money(stundenfaktor)} · {fmtPct(kalkRuecklage)}
                  </div>
                )}
              </div>
            </div>

            <div
              className="pe-zk-custom mt-1 w-full max-w-xl flex flex-nowrap items-center justify-center gap-2 rounded-xl border border-[rgba(220,182,138,0.35)] bg-[rgba(18,18,18,0.85)] px-3 py-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[0.65rem] font-bold uppercase tracking-wide zk-muted shrink-0">
                Bedienzeit
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  className="step-btn !w-7 !h-7 !text-sm"
                  onClick={() => setCustomMinutes((m) => Math.max(0, m - 5))}
                  title="Verringern (0 = aus)"
                >
                  −
                </button>
                <input
                  ref={minsRef}
                  type="number"
                  className="mat-input !w-14 !text-sm"
                  value={customMinutes}
                  min={0}
                  max={480}
                  step={5}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || raw === '-') {
                      setCustomMinutes(0);
                      return;
                    }
                    setCustomMinutes(Math.max(0, Math.min(480, parseFloat(raw) || 0)));
                  }}
                />
                <span className="text-xs font-bold zk-muted">Min</span>
                <button
                  type="button"
                  className="step-btn !w-7 !h-7 !text-sm"
                  onClick={() => setCustomMinutes((m) => Math.min(480, m + 5))}
                >
                  +
                </button>
                <button
                  type="button"
                  className="step-btn !h-7 !min-w-[3.25rem] !px-2 !text-[0.58rem] !font-black uppercase tracking-wider shrink-0"
                  onClick={() => setCustomMinutes(0)}
                  disabled={customMinutes <= 0}
                  title="Bedienzeit auf 0 zurücksetzen"
                >
                  Reset
                </button>
              </div>
              <span className="zk-gold font-black text-sm shrink-0">➔</span>
              <div className="flex items-baseline gap-1.5 shrink-0 min-w-[9.5rem]">
                <span className="text-xs zk-muted shrink-0">Preis:</span>
                {/* Immer gleiche Breite — auch bei 0,00 kein Layout-Sprung */}
                <span className="text-base font-black zk-gold font-numeric tabular-nums inline-block min-w-[7.25rem] text-left">
                  {money(customPrice)}
                </span>
              </div>
            </div>
          </div>
        )}

        {mode === 'cost' && (
          <div className="animate-[fadeIn_0.35s_ease-out]">
            <div
              className="pe-zk-banner mb-2 flex flex-col items-center rounded-xl border border-[rgba(220,182,138,0.35)] bg-[rgba(18,18,18,0.85)] px-3 py-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[0.65rem] font-extrabold uppercase tracking-[0.12em] zk-muted">
                Brutto-Stundensatz am Bedienplatz
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className="step-btn" onClick={() => adjustRate(-5)}>
                  −
                </button>
                <div className="flex items-baseline">
                  <input
                    ref={rateCostRef}
                    type="number"
                    className="rate-input !w-[96px]"
                    value={rate}
                    min={5}
                    max={999}
                    step={5}
                    onChange={(e) => setGlobalRate(parseFloat(e.target.value) || 5)}
                  />
                  <span className="zk-gold text-lg font-black">{currencyCode}</span>
                </div>
                <button type="button" className="step-btn" onClick={() => adjustRate(5)}>
                  +
                </button>
              </div>
              <div className="text-xs zk-muted">
                Nettoumsatz:{' '}
                <strong className="text-[var(--zk-text)]">
                  {costStep >= 1 ? money(calc.netto) : '—'}
                </strong>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-3 items-start">
              <div className="flex flex-col gap-1.5">
                {[
                  {
                    key: 1,
                    cls: 'tax',
                    title: `1. Mehrwertsteuer (${fmtPct(factors.mwstPct, 0)})`,
                    sub: 'Gehört dem Finanzamt, nicht deinem Salon',
                    val: `− ${money(calc.mwstVal)}`,
                    minus: true,
                  },
                  {
                    key: 2,
                    cls: 'mat',
                    title: `2. Materialeinsatz (Faktor ${fmtDe(factors.matFaktor, 2)})`,
                    sub: null,
                    val: `− ${money(calc.matTotal)}`,
                    minus: true,
                    material: true,
                  },
                  {
                    key: 3,
                    cls: 'overhead',
                    title: `3. Feste Gemeinkosten (${fmtPct(factors.gkPct)})`,
                    sub: 'Miete, Strom, Wasser, BG, Steuerberater, Kasse, Software',
                    val: `− ${money(calc.overhead)}`,
                    minus: true,
                  },
                  {
                    key: 4,
                    cls: 'wage-owner',
                    title: `4. Unpr. Unternehmerlohn (${fmtPct(factors.ulPct)})`,
                    sub: 'Salonleitung, Büro, Organisation, Einkauf',
                    val: `− ${money(calc.ownerWage)}`,
                    minus: true,
                  },
                  {
                    key: 5,
                    cls: 'loss',
                    title: `5. Kalk. Verlust & Leerlauf (${fmtPct(factors.vlPct)})`,
                    sub: 'No-Shows, kurzfristige Absagen, unproduktive Zeiten',
                    val: `− ${money(calc.loss)}`,
                    minus: true,
                  },
                  {
                    key: 6,
                    cls: 'subtotal',
                    title: '✦ Übrig für Lohnkosten',
                    sub: 'Bleibt übrig für Lohn, Lohnnebenkosten & Rücklagen',
                    val: money(calc.subtotal),
                    minus: false,
                  },
                  {
                    key: 7,
                    cls: 'wage-team',
                    title: '6. Lohn & Nebenkosten (Vollkosten)',
                    sub: `${money(factors.teamBase)} Teamlohn (ø SV ${fmtPct(factors.svPct)} | URL. ${fmtPct(factors.urlPct)})`,
                    val: `− ${money(factors.teamTotal)}`,
                    minus: true,
                  },
                ].map((card) => {
                  const visible = costStep >= card.key;
                  return (
                    <div
                      key={card.key}
                      className={`pe-zk-deduct deduct-card ${card.cls} ${visible ? 'visible' : ''}`}
                    >
                      <div className="flex flex-col gap-0.5 min-w-0 pr-2">
                        <div className="text-[0.8rem] font-bold flex items-center gap-1.5 flex-wrap leading-tight">
                          {card.title}
                        </div>
                        {card.material ? (
                          <div className="flex flex-col gap-1 mt-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs zk-muted">Grammzahl:</span>
                              <input
                                ref={gramsRef}
                                type="number"
                                className="mat-input"
                                value={matGrams}
                                min={0}
                                max={9999}
                                step={1}
                                onChange={(e) =>
                                  setMatGrams(Math.max(0, parseFloat(e.target.value) || 0))
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-xs zk-muted">g</span>
                            </div>
                            <div className="text-[0.65rem] zk-muted leading-snug">
                              {factors.matCount > 0 ? (
                                <>
                                  Ø aktiv{' '}
                                  <strong className="zk-gold">{money(factors.avg100)}</strong>
                                  /100g × Faktor {fmtDe(factors.matFaktor, 2)}
                                </>
                              ) : (
                                <>Rechts unten Material aktivieren.</>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-[0.65rem] zk-muted leading-snug">{card.sub}</div>
                        )}
                      </div>
                      <div
                        className={`text-[0.95rem] font-black whitespace-nowrap min-w-[72px] text-right ${
                          visible
                            ? card.minus
                              ? 'text-red-400'
                              : 'zk-gold'
                            : 'text-slate-500 tracking-widest'
                        }`}
                      >
                        {visible ? card.val : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pe-zk-side flex flex-col gap-2">
                <div className="pe-zk-team rounded-xl border border-[rgba(220,182,138,0.3)] bg-[rgba(18,18,18,0.85)] p-2.5">
                  <div className="flex justify-between items-center mb-1 pb-1 border-b border-[rgba(220,182,138,0.2)]">
                    <h3 className="zk-gold text-[0.7rem] font-extrabold uppercase tracking-wide">
                      Teamstundenlohn
                    </h3>
                    <div className="text-base font-black font-numeric text-[var(--zk-text)]">{money(factors.teamBase)}</div>
                  </div>
                  <ul className="text-[0.78rem] zk-muted space-y-1 font-medium">
                    <li className="flex justify-between gap-2">
                      <span>Basis-Bruttolohn:</span>
                      <strong className="text-[var(--zk-text)] font-black">{money(factors.teamBase)}</strong>
                    </li>
                    <li className="flex justify-between gap-2">
                      <span>+ {fmtPct(factors.urlPct)} Urlaub & Feiertage:</span>
                      <strong className="text-[var(--zk-text)] font-black">
                        + {money(factors.urlaubVal)}
                      </strong>
                    </li>
                    <li className="flex justify-between gap-2">
                      <span>+ {fmtPct(factors.svPct)} AG-Sozialabgaben:</span>
                      <strong className="text-[var(--zk-text)] font-black">
                        + {money(factors.sozialVal)}
                      </strong>
                    </li>
                  </ul>
                  <div className="mt-1.5 pt-1.5 border-t border-dashed border-[rgba(220,182,138,0.3)] flex justify-between items-baseline text-[0.8rem] font-extrabold zk-gold">
                    <span>Lohn gesamt:</span>
                    <span className="font-numeric">{money(factors.teamTotal)}</span>
                  </div>
                </div>

                <div
                  className={`final-card ${costStep >= 8 ? `visible ${resultStatus.cls}` : ''}`}
                >
                  <div
                    className={`inline-block text-[0.65rem] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full mb-1.5 ${
                      costStep >= 8
                        ? resultStatus.cls === 'status-red'
                          ? 'bg-red-500 text-white'
                          : resultStatus.cls === 'status-orange'
                            ? 'bg-orange-500 text-white'
                            : resultStatus.cls === 'status-yellow'
                              ? 'bg-yellow-500 text-black'
                              : resultStatus.cls === 'status-green'
                                ? 'bg-green-500 text-black'
                                : 'bg-purple-500 text-white'
                        : 'bg-slate-600 text-white'
                    }`}
                  >
                    {costStep >= 8 ? resultStatus.badge : 'BEREIT'}
                  </div>
                  <div className="text-xs font-bold zk-muted mb-0.5">Reingewinn / Stunde</div>
                  <div
                    className={`text-[1.85rem] font-black leading-none mb-0.5 font-numeric ${
                      costStep >= 8
                        ? resultStatus.cls === 'status-red'
                          ? 'text-red-500'
                          : resultStatus.cls === 'status-orange'
                            ? 'text-orange-500'
                            : resultStatus.cls === 'status-yellow'
                              ? 'text-yellow-500'
                              : resultStatus.cls === 'status-green'
                                ? 'text-green-500'
                                : 'text-purple-400'
                        : ''
                    }`}
                  >
                    {costStep >= 8 ? money(calc.finalProfit) : '—'}
                  </div>
                  <div className="text-sm font-extrabold mb-1">
                    {costStep >= 8 ? `${fmtPct(calc.marginPercent)} Rücklage` : '— % Rücklage'}
                  </div>
                  <p className="text-[0.68rem] text-slate-300 dark:text-slate-300 max-w-xs mx-auto leading-snug">
                    {costStep >= 8
                      ? resultStatus.desc
                      : 'Weiter klicken, um Kosten schrittweise abzuziehen.'}
                  </p>
                </div>

                <div
                  className="pe-zk-mat-panel pe-zk-mat-list rounded-xl border border-[rgba(56,189,248,0.35)] bg-[rgba(18,18,18,0.85)] p-2.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-end gap-2 mb-1.5 pb-1 border-b border-[rgba(56,189,248,0.25)]">
                    <h3 className="text-[0.7rem] font-extrabold uppercase tracking-wide text-sky-300">
                      Material aktivieren
                    </h3>
                    <span className="text-[0.55rem] font-black uppercase tracking-widest zk-muted shrink-0">
                      Preis /100g
                    </span>
                  </div>
                  {(materialData || []).length === 0 ? (
                    <div className="text-[0.65rem] zk-muted leading-snug py-1">
                      Keine Produkte — unten im Materialrechner anlegen.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1 max-h-[9rem] overflow-y-auto pr-0.5">
                      {(materialData || []).map((m: any) => {
                        const per100 =
                          Number(m.gebinde) > 0
                            ? (Number(m.ekPreis) / Number(m.gebinde)) * 100
                            : 0;
                        const checked = (activeMaterialIDs || []).includes(m.id);
                        return (
                          <label
                            key={m.id}
                            className="flex items-center justify-between gap-2 text-[0.72rem] cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMaterial(m.id)}
                                className="accent-[#38bdf8] shrink-0"
                              />
                              <span className="truncate zk-muted">{m.name}</span>
                            </span>
                            <span className="zk-gold font-semibold whitespace-nowrap shrink-0 tabular-nums">
                              {money(per100)}
                              <span className="zk-muted text-[0.55rem] ml-0.5">/100</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-1.5 pt-1.5 border-t border-dashed border-[rgba(56,189,248,0.25)] text-[0.65rem] zk-muted leading-snug">
                    {factors.matCount > 0 ? (
                      <>
                        Ø aktiv{' '}
                        <strong className="zk-gold">{money(factors.avg100)}</strong>
                        /100g × Faktor {fmtDe(factors.matFaktor, 2)}
                      </>
                    ) : (
                      <>Produkte anhaken — dann links Grammzahl eingeben.</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <div className="pe-zk-footer shrink-0 px-2.5 pb-2 pt-1.5 border-t border-[rgba(220,182,138,0.2)] bg-[rgba(10,10,10,0.96)]">
        <div className="text-[0.68rem] zk-muted mb-1 text-center sm:text-left truncate">
          Fokus: <span className="zk-gold font-extrabold">{stepLabel}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-1.5 sm:items-center sm:justify-between">
          <div className="pe-zk-controls flex flex-wrap gap-1.5 justify-center items-center">
            <button type="button" className="nav-btn" onClick={prevStep}>
              ❮ Zurück
            </button>
            <button
              type="button"
              className="nav-btn !border-[rgba(220,182,138,0.3)] !text-[var(--zk-muted)]"
              onClick={resetStep}
            >
              ↺ Reset
            </button>
            <button type="button" className="nav-btn" onClick={nextStep}>
              Weiter ❯
            </button>
          </div>
          <div className="pe-zk-presets flex gap-1 overflow-x-auto max-w-full rounded-full border border-[rgba(220,182,138,0.3)] bg-[rgba(15,15,15,0.9)] px-2 py-1">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`preset-btn ${rate === p ? 'active' : ''}`}
                onClick={() => setGlobalRate(p)}
              >
                {p} {currencyCode}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
